<template>
  <div class="ak-tabs" :class="mainClasses">
    <nav class="ak-tabs__nav" :class="navClasses">
      <ul class="ak-tabs__nav-list">
        <li
          v-for="(tabItem, index) in tabItems"
          v-show="tabItem.visible"
          :key="index"
          :class="[
            {
              'is-active': activeTab === index,
              'is-disabled': tabItem.disabled,
            },
            tabNavClasses,
          ]"
          class="ak-tabs__nav-item"
        >
          <ak-slot-component
            v-if="tabItem.slots.header"
            :component="tabItem"
            :class="tabNavLinkClasses"
            class="ak-tabs__nav-link"
            name="header"
            tag="a"
            @click="tabClick(index)"
          />
          <a
            v-else
            class="ak-tabs__nav-link"
            :class="tabNavLinkClasses"
            @click="tabClick(index)"
          >
            <ak-pack spacing="tiny">
              <ak-pack-item>
                <ak-icon
                  v-if="tabItem.icon"
                  :type="tabItem.icon"
                  :size="iconSize"
                  class="block"
                />
              </ak-pack-item>
              <ak-pack-item grow>{{ tabItem.label }}</ak-pack-item>
            </ak-pack>
          </a>
        </li>
        <template v-if="$slots.navRight">
          <li class="ak-tabs__nav-item-right">
            <slot name="navRight" />
          </li>
        </template>
      </ul>
    </nav>
    <section
      class="ak-tabs__content"
      :class="{
        'is-transitioning': isTransitioning,
        'overflow-visible': tabContentClasses,
      }"
    >
      <slot />
    </section>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch } from 'vue'

import Icon from '../icon/Icon.vue'
import Pack from '../pack/Pack.vue'
import PackItem from '../pack/PackItem.vue'
import SlotComponent from '../../utils/SlotComponent'
import { isObject } from '../../utils/helpers'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-tabs>
   *    <ak-tabs-item label="First Tab">
   *      Content
   *    </ak-tabs-item>
   *    <ak-tabs-item label="Second Tab">
   *      Content
   *    </ak-tabs-item>
   *    <ak-tabs-item label="Third Tab">
   *      Content
   *    </ak-tabs-item>
   * </ak-tabs>
   */
  name: 'AkTabs',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Icon.name]: Icon,
    [SlotComponent.name]: SlotComponent,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
  },

  provide() {
    return {
      akTabs: this,
    }
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Number}
     * @description Binding value, tab index
     * */
    modelValue: {
      type: [Number, Object],
      default: 0,
    },

    /**
     * @type {Boolean}
     * @description Make tab full width
     */
    justified: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Make tab nav narrow style
     */
    narrowNav: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Type/Style of the tab, optional
     */
    type: {
      type: String,
      default: undefined,
    },

    /**
     * @type {String}
     * @description Size of the icon in the tab, optional
     */
    iconSize: {
      type: String,
      default: undefined,
    },

    /**
     * @type {String}
     * @description Position of the tab, optional
     */
    position: {
      type: String,
      default: undefined,
    },

    /**
     * @type {Boolean}
     * @description Display the tabs vertically. The content will be
     * placed at right.
     */
    vertical: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Controls the background of the tabs nav.
     */
    navBackground: {
      type: String,
      default: null,
    },

    /**
     * @type {String}
     * @description Controls the color of the tabs nav.
     */
    navColor: {
      type: String,
      default: null,
    },

    /**
     * @type {String}
     * @description Removes the bottom border
     */
    noBorder: {
      type: Boolean,
      default: false,
    },
    inPanel: {
      type: Boolean,
      default: false,
    },
    borderBottom: {
      type: Boolean,
      default: false,
    },
    visibleOverflow: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:modelValue', 'change'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const contentHeight = ref(0)
    const isTransitioning = ref(false)
    const isTabs = ref(true)
    const tabItems = ref([])
    const tabContentClasses = ref(props.visibleOverflow)

    /**
     * @type {Object}
     * @description The model property for the active tab
     */
    const activeTab = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      },
    })

    /**
     * @returns {Object}
     * @description Returns an object containing the classes for the root
     * element of the component.
     */
    const mainClasses = computed(() => {
      return {
        'is-vertical': props.vertical,
        [props.position]: props.position && props.vertical,
      }
    })

    /**
     * @returns {Array}
     * @description Returns an array of classes to be applied to the tabs
     * navigation bar
     */
    const navClasses = computed(() => {
      return [
        props.type,
        props.size,
        {
          [props.position]: props.position && !props.vertical,
          'ak-tabs__nav--justified': props.justified,
          'bb-none': props.noBorder,
          'tabs-nav--in-panel': props.inPanel,
          bb: props.borderBottom,
        },
      ]
    })
    /**
     * @returns {Object}
     * @description Returns the classes for the tabs nav background
     */
    const tabNavClasses = computed(() => {
      return {
        [`bg-${props.navBackground}`]: props.navBackground,
      }
    })

    /**
     * @returns {Object}
     * @description Returns the classes for the tabs nav background
     */
    const tabNavLinkClasses = computed(() => {
      return {
        'ak-tabs__nav-link--narrow': props.narrowNav,
        [`color-${props.navColor}`]: props.navColor,
      }
    })

    /**
     * @param {Number}
     * @description Change the active tab and emit change event.
     */
    const changeTab = (index) => {
      if (activeTab.value === index) return
      activeTab.value = index
    }

    /**
     * @param {Number} value
     * @description Tab click listener, emit input event and change active tab.
     */
    const tabClick = (index) => {
      changeTab(index)
    }

    /**
     * @type {Function}
     * @description Registers a new tab in the parent tabs component. This will
     * allow the parent to manage the child components as they get added.
     */
    const registerTab = (tab) => {
      tabItems.value.push(tab)
    }

    /**
     * @type {Function}
     * @description Un-registers an existing tab in the parent tabs component. This
     * will allow the parent to remove a child tab when it is no longer needed.
     */
    const unregisterTab = (id) => {
      const idx = tabItems.value.findIndex((i) => i.uid === id)

      /* istanbul ignore else */
      if (idx >= 0) {
        tabItems.value.splice(idx, 1)
      }
    }

    onMounted(() => {
      const current = tabItems.value[activeTab.value]
      if (isObject(current)) {
        current.activate()
      } else {
        changeTab(0)
      }
    })

    watch(activeTab, (newVal, oldVal) => {
      try {
        tabItems.value[oldVal].deactivate()
      } catch (e) {
        /* istanbul ignore next */
      }

      const next = tabItems.value[newVal]
      next.activate()
      emit('change', newVal)
    })

    return {
      contentHeight,
      isTransitioning,
      tabItems,
      isTabs,
      activeTab,
      mainClasses,
      navClasses,
      tabNavClasses,
      tabNavLinkClasses,
      tabContentClasses,
      changeTab,
      tabClick,
      registerTab,
      unregisterTab,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
@import '../../scss/_mixins/_custom';
.ak-tabs {
  display: flex;
  flex-direction: column;
  &__nav {
    border-bottom-width: 4px;
    white-space: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    flex-shrink: 0;
  }

  &__nav-list {
    display: flex;
  }

  &__nav-item {
    display: flex;
    .ak-tabs__nav--justified & {
      flex-grow: 1;
      flex-basis: 0;
    }

    &-right {
      margin-left: auto;
    }
  }

  &__nav-link {
    display: block;
    width: 100%;
    height: 100%;
    text-align: center;
    opacity: 0.8;
    padding: threequarts($spacing) 1.25rem calc(#{threequarts($spacing)} - 4px);
    cursor: pointer;
    border-bottom: 4px solid transparent;
    font-weight: bold;
    color: $color-text;
    font-size: $font-size-small;
    transition: $transition;

    &:hover,
    &:focus {
      opacity: 1;
      background-color: rgba(0, 0, 0, 0.075);
      border-color: rgba(0, 0, 0, 0.075);
    }
    .is-active & {
      border-color: $color-primary;
      color: $color-grey;
      opacity: 1;
    }

    &--narrow {
      padding: threequarts($spacing-small) 1.25rem
        calc(#{threequarts($spacing-small)} - 4px);
    }
  }

  &__content {
    flex-grow: 1;
    overflow: hidden;
    position: relative;
  }
}
</style>
